import { useEffect, useState, useMemo, useCallback, SyntheticEvent, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { debounce } from 'lodash';
import { LocalErrorBoundary } from 'components/ErrorBoundary';

import { getAuditReport, useReportsStore } from 'hooks/useReportsStore';

import { isEmpty, uniqBy } from 'lodash';
import Loading from 'components/Loading';
import { HoneReportTypes, MENU_ICONS } from '../../../constants';
import {
  abilityCan,
  currencyFormatter2Decimals,
  percentageFormatter,
  reportTitleToUrl,
  showToast,
  sortReportTitles,
  TOAST_ERROR,
} from 'lib/utils';

import toast from 'react-hot-toast';
import { HoneReportTimeframes } from 'domain/models';
import { performReportUpdate, useActiveReportStore } from 'hooks/useActiveReportStore';
import { shallow } from 'zustand/shallow';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocationsStore } from 'hooks/useLocationsStore';

import { useQueryState } from 'hooks/useQueryState';
import { useReportGraphStore } from 'hooks/useReportGraphStore';

import { QueryStatus } from '@tanstack/react-query';
import qs from 'querystring';

import {
  commonUtil,
  ComparisonRequest,
  ComparisonType,
  extractReportIds,
  GenerateRangesRequest,
  HoneAbilityActions,
  HoneAbilitySubjects,
  ReportConfiguration,
  ReportRequest,
  ReportTimeframe,
  ReportTimeframes,
  StartEndDates,
} from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';

import { useUserLocationsQuery } from '../../../hooks/useUserLocationsQuery';

import KSHeader from '../../components/KSHeader';
import { KSCompareLocationsModal } from '../../components/KSCompareLocationsModal';
import { generateRanges } from '@hone-automation/common/lib/reportUtil';

import STBTools from 'presentation/components/SectionToolbar/STBTools';

import { SlidingPane } from 'components/SlidingPane';
import ValidationReportResults from 'components/ValidationReportResults';
import { differenceInSeconds, parseJSON } from 'date-fns';
import { confirmAlert } from 'react-confirm-alert';

import { getInitialReportDateRanges } from 'lib/reportUtils';

import { Report } from 'lib/honeTableUtils';
import useActiveActions from 'hooks/useActiveActions';
import NewTable from 'presentation/components/NewTable';
import { useTemplatesRangesQuery } from '../../../hooks/reports/useTemplatesRangesQuery';

import ReportPLDates from '../../components/ReportPLDatesModal/ReportPLDates';
import { useGetReportConfiguration } from '../../../hooks';
import { useSendHubspotEmail } from 'hooks/reports/useSendHubspotEmail';
import { useReviewFinancials } from 'hooks/reports/useReviewFInancials';
import { IconAuditNew, IconEmailNew, IconValidateNew } from 'components/Icons';
import { ActionProps } from 'presentation/components/SectionToolbar/STBTools/STBTools';

interface Props {
  reports: Map<string, HoneReportSummary[]>;
  columnHeaderTitle?: string;
  customHeaders?: string[];
  status: QueryStatus;
}

function PLReportView({ reports, columnHeaderTitle = '', customHeaders, status }: Props) {
  // URL Params
  const { locationId, reportId, reportType } = useParams<{
    locationId: string;
    reportId: string;
    reportType: string;
  }>();

  // AI Report -> To be placed under feature flags
  const enableAiReport = true;

  // Data queries
  const { data: availableTemplates } = useTemplatesRangesQuery();
  const { data: reportConfiguration } = useGetReportConfiguration(locationId);
  const { data: userLocations } = useUserLocationsQuery();

  // Auth & Permissions
  const { currentLocationAbilities } = useAuthContext();
  const isBookkeeper = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  // Router hooks
  const location = useLocation();
  const navigateTo = useNavigate();

  // Location state
  const { currentLocationId, currentLocation } = useLocationsStore(
    state => ({ currentLocationId: state.currentLocationId, currentLocation: state.currentLocation }),
    shallow
  );
  const currentLocationUser = (userLocations as HoneLocationUser[])?.find(loc => loc.locationId === currentLocationId);

  // UI state hooks
  const { activeActions } = useActiveActions();
  const validationReportData = useActiveReportStore(state => state.currentReport?.validation);

  // URL query parameters
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isPLReport = ['income-statement', 'monthly', 'weekly'].includes(reportType!);

  // Query state hooks
  const [auditMode, setAuditMode] = useQueryState('audit');
  const [consolidated] = useQueryState<string>('consolidated');
  const [validation] = useQueryState('validate');
  const [timeframe] = useQueryState<string>('timeframe');
  const [chartCollapsed, setChartCollapsed] = useQueryState('chartCollapsed', 'false');
  const [dateRange, setDateRange] = useQueryState<string>('dateRange');

  // Local component state
  const [disabledReviewFinancials, setDisabledReviewFinancials] = useState<boolean>(true);
  const [disabledSendEmail, setDisabledSendEmail] = useState<boolean>(true);
  const [_, setLastPayloadString] = useState<string | null>(null);
  const [showValidationPane, setShowValidationPanel] = useState<boolean>(false);
  const [validateReportError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [redraw, setRedraw] = useState<boolean>(true);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [paramList, setParamList] = useState<any>(Object.fromEntries(urlParams.entries()));

  // Derived state
  const showPercentageValues = activeActions.includes('lines');
  const showMoneyValues = activeActions.includes('bars');

  // Mutations
  const { mutate: sendEmail, isPending: sendEmailPending } = useSendHubspotEmail();
  const { mutate: reviewFinancials, isPending: reviewFinancialsLoading } = useReviewFinancials();

  useEffect(() => {
    if (!validationReportData) {
      setDisabledReviewFinancials(true);
      setDisabledSendEmail(true);
      return;
    }
    //validate this validationReportData
    const resultsHasErrors = validationReportData?.error > 0 || validationReportData?.fail > 0;
    setDisabledReviewFinancials(resultsHasErrors);
    setDisabledSendEmail(resultsHasErrors);
  }, [validationReportData]);

  useEffect(() => {
    if (validation) {
      setShowValidationPanel(true);
    }
  }, [validation]);

  useEffect(() => {
    if (isPLReport) {
      setChartCollapsed('true');
    }
  }, [reportType, isPLReport]);

  const reportsError = useReportsStore(state => state.error);
  const {
    setOpenDateRangeModal,
    openDateRangeModal,
    reportsConfiguration,
    setAiReport,
    aiReport,
    setAiEditor,
    aiEditor,
    reportQueue,
    setReportQueue,
  } = useReportsStore(
    state => ({
      setOpenDateRangeModal: state.setOpenDateRangeModal,
      openDateRangeModal: state.openDateRangeModal,
      reportsConfiguration: state.reportsConfiguration,
      setAiReport: state.setAiReport,
      aiReport: state.aiReport,
      setAiEditor: state.setAiEditor,
      aiEditor: state.aiEditor,
      reportQueue: state.reportQueue,
      setReportQueue: state.setReportQueue,
    }),
    shallow
  );

  const {
    reportStatus,
    allCategories,
    getGroupReport,
    setYearPlotted,
    setRefreshingReport,
    rangeNoData,
    setStatus,
    currentReport,
    enableMultiLocation,
    setEnableMultiLocation,
    setReportPayload,
  } = useActiveReportStore(
    state => ({
      setStatus: state.setStatus,
      rangeNoData: state.rangeNoData,
      reportStatus: state.status,
      allCategories: state.allCategories,
      enableMultiLocation: state.enableMultiLocation,
      setRefreshingReport: state.setRefreshingReport,
      getGroupReport: state.getGroupReport,
      setYearPlotted: state.setYearPlotted,
      setEnableMultiLocation: state.setEnableMultiLocation,
      currentReport: state.currentReport,
      setReportPayload: state.setReportPayload,
    }),
    shallow
  );

  const selectedReport = currentReport as unknown as HoneReportSummary;
  const isYtd = selectedReport?.timeframe === HoneReportTimeframes.YTDMonthly;

  const [statusUpdates] = useState<Record<string, string>>({});

  const allReports = reports.get(isPLReport ? 'pl' : reportType!) || [];
  const titles = uniqBy(allReports, 'title');

  const uniqueTitles = sortReportTitles(titles);

  const [titleFilter] = useQueryState<string>('type', reportTitleToUrl(uniqueTitles[0]?.title || ''));
  const [templateId] = useQueryState<string>('templateId');

  useEffect(() => {
    if (availableTemplates && availableTemplates?.length === 0) {
      toast.error('There are not any templates available at this location');
      navigateTo(`/app/location/${locationId}/dashboard`);
      return;
    }
    if (templateId) return;
    const currentTemplateId = availableTemplates?.[0].id;
    if (!templateId && currentTemplateId) {
      urlParams.set('templateId', currentTemplateId!);
      urlParams.set('chartCollapsed', 'true');
      urlParams.set('type', 'P&L Comparison');
      urlParams.set('difference', 'false');
      urlParams.set('reverse', 'false');
      urlParams.set('budget', 'false');
      urlParams.set('customDateRange', 'false');
      urlParams.set('breakdownPeriods', 'false');
      urlParams.set('timeframe', 'Week');
      // Don't set comparison defaults on initial load - let the user explicitly enable comparison
      // urlParams.set('comparisonType', 'Prior Period');
      // urlParams.set('comparisonPeriods', '4');
      // urlParams.set('compareEnabled', 'true');

      navigateTo(`/app/location/${locationId}/report/${reportType}?${urlParams.toString()}`);
    }
  }, [locationId, templateId, availableTemplates]);

  const filteredTitleFilter = templateId
    ? reportTitleToUrl(uniqueTitles.find(props => props.templateId === templateId)?.title || '')
    : titleFilter.split('&')[0];

  let filteredReports = allReports;
  if (isPLReport && titleFilter) {
    filteredReports = filteredReports.filter(({ title, timeframe }) => {
      if ((timeframe.includes('YTD') && reportType === 'income-statement') || timeframe.toLowerCase() === reportType) {
        return reportTitleToUrl(title) === filteredTitleFilter;
      }
      return false;
    });
  }

  useEffect(() => {
    rangeNoData && navigateTo(`/app/location/${currentLocationId}/report/${reportType}`, { replace: true });
  }, [rangeNoData]);

  useEffect(() => {
    if (!isYtd) return;
    // REVIEW: old report from 2020 on dev for Allegria does not have startDateText making the UI crash
    if (!selectedReport?.startDateText) return;
    const yearToPlot = Number(selectedReport.startDateText.split('-')[0]);
    setYearPlotted(yearToPlot);
  }, [selectedReport]);

  // Use refs to stabilize URL param reads and prevent null/value toggling
  const totalParsedRef = useRef(urlParams.get('total') || 'false');
  const differenceParsedRef = useRef(urlParams.get('difference') || 'false');
  const reverseParsedRef = useRef(urlParams.get('reverse') || 'false');
  const breakdownPeriodsParsedRef = useRef(urlParams.get('breakdownPeriods') || 'false');
  const comparisonTypeRef = useRef(urlParams.get('comparisonType') || 'Prior Period');
  const comparisonPeriodsRef = useRef(urlParams.get('comparisonPeriods') || '0');
  const compareLocationsRef = useRef(urlParams.get('compareLocations'));
  const budgetRef = useRef(urlParams.get('budget') || 'false');
  const validateParsedRef = useRef(urlParams.get('validate'));

  // Update refs when URL actually changes (not on every render)
  useEffect(() => {
    const newTotal = urlParams.get('total');
    const newDifference = urlParams.get('difference');
    const newReverse = urlParams.get('reverse');
    const newBreakdownPeriods = urlParams.get('breakdownPeriods');
    const newComparisonType = urlParams.get('comparisonType');
    const newComparisonPeriods = urlParams.get('comparisonPeriods');
    const newCompareLocations = urlParams.get('compareLocations');
    const newBudget = urlParams.get('budget');
    const newValidate = urlParams.get('validate');

    if (newTotal !== null) totalParsedRef.current = newTotal;
    if (newDifference !== null) differenceParsedRef.current = newDifference;
    if (newReverse !== null) reverseParsedRef.current = newReverse;
    if (newBreakdownPeriods !== null) breakdownPeriodsParsedRef.current = newBreakdownPeriods;
    if (newComparisonType !== null) comparisonTypeRef.current = newComparisonType;
    if (newComparisonPeriods !== null) comparisonPeriodsRef.current = newComparisonPeriods;
    if (newCompareLocations !== null) compareLocationsRef.current = newCompareLocations;
    if (newBudget !== null) budgetRef.current = newBudget;
    if (newValidate !== null) validateParsedRef.current = newValidate;
  }, [queryString]); // Only update when query string actually changes

  const totalParsed = totalParsedRef.current;
  const differenceParsed = differenceParsedRef.current;
  const reverseParsed = reverseParsedRef.current;
  const breakdownPeriodsParsed = breakdownPeriodsParsedRef.current;
  const comparisonType = comparisonTypeRef.current;
  const comparisonPeriods = comparisonPeriodsRef.current;
  const compareLocations = compareLocationsRef.current;
  const budget = budgetRef.current;
  const validateParsed = validateParsedRef.current;

  const generateReportPayload = ({
    currentLocation,
    dateRange,
    comparisonPeriods,
    compareLocations,
    availableTemplates,
  }: any) => {
    if (!currentLocation) return;

    const type: 'P&L Comparison' | 'Balance Sheet' = 'P&L Comparison';
    const [startDate, endDate] = urlParams.get('dateRange')?.split(',') || [];
    const limit = undefined;

    const comparison: ComparisonRequest = {
      comparisonType: comparisonType as ComparisonType,
      comparisonPeriods: Number(comparisonPeriods),
    };

    const now = commonUtil.toShortString(new Date());
    const rangeEnd = availableTemplates?.length ? availableTemplates?.[0].range.end : now;

    const request = Object.assign(
      new GenerateRangesRequest(ReportTimeframes.Week, currentLocation?.weekStart, reportsConfiguration?.data),
      {
        limit: 2,
        end: rangeEnd > now ? now : rangeEnd,
      }
    );
    const newDateRanges: StartEndDates[] = generateRanges(request);

    const locations = compareLocations
      ? compareLocations?.split(',').map((location: string) => location)
      : [String(locationId)];

    const dateParsed = getInitialReportDateRanges(newDateRanges);

    const reportPayload: ReportRequest = {
      type,
      locations,
      templateId: templateId,
      startDate: dateRange ? startDate : dateParsed?.start,
      endDate: dateRange ? endDate : dateParsed?.end,
      total: totalParsed === 'true',
      difference: differenceParsed === 'true',
      limit,
    };

    // Only add comparison if it's explicitly enabled and has periods > 0
    if (comparisonType && Number(comparisonPeriods) > 0) {
      reportPayload.comparison = comparison;
    }

    setReportPayload(reportPayload);

    return reportPayload;
  };

  const isAggrSideBySide =
    compareLocations && (!consolidated || consolidated === 'false') ? compareLocations?.split(',').length > 1 : false;

  const handleValidateReport = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const validate = searchParams.get('validate');
    if (validate) {
      setShowValidationPanel(true);
      return;
    }
    searchParams.set('validate', 'true');
    const newSearch = searchParams.toString();

    navigateTo({
      search: newSearch,
    });
  };

  const toggleAuditMode = () => {
    const nextState = auditMode === 'true' ? 'false' : 'true';
    setAuditMode(nextState);
    return nextState;
  };

  const handleAuditReport = async (toggle = true) => {
    let next: 'true' | 'false' = 'false';
    if (toggle) {
      next = toggleAuditMode();
    }
    if ((!toggle || next === 'true') && selectedReport?.id) {
      await getAuditReport(selectedReport.id);
    } else {
      useReportsStore.setState({ auditReport: null });
    }
  };

  useEffect(() => {
    if (currentReport) {
      useActiveReportStore.setState({ currentReport: { ...currentReport, validation: undefined } });
    }
    if (auditMode === 'true') {
      handleAuditReport(false);
    }
    () => {
      setDisabledReviewFinancials(true);
      setDisabledSendEmail(true);
    };
  }, []);

  const handleRefreshReport = async (e: SyntheticEvent<HTMLButtonElement>) => {
    try {
      const target = e.currentTarget;
      // allow only every two minutes
      const selectedReportId = selectedReport?.id;
      const updatedAt = currentReport?.updatedAt ? currentReport?.updatedAt : selectedReport?.updatedAt;
      const difference = updatedAt ? differenceInSeconds(new Date(), parseJSON(updatedAt)) : 0;

      const refreshTimeAllowed = 60 * 5;
      if (difference < refreshTimeAllowed) {
        showToast(`Report can be refreshed again in ${refreshTimeAllowed - difference} seconds`, TOAST_ERROR);
        return;
      }

      if (target) {
        target.disabled = true;
      }

      setRefreshingReport(true);
      const periodStartEndDates = currentReport?.dates;
      const extractedReportIds = extractReportIds(periodStartEndDates!);

      const reportIds = extractedReportIds;
      await performReportUpdate(selectedReport!, reportIds);

      if (target) {
        target.disabled = false;
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      toast.error('Error refreshing report');
    } finally {
      setRefreshingReport(false);
    }
  };

  const handleReviewFinancials = async () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-ui">
            <h3 className="text-center">Are you sure financials are ready to review?</h3>
            <button className="button ReportControls-button mr-2" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button ReportControls-button-alert"
              onClick={() => {
                onClose();
                reviewFinancials();
              }}
              disabled={reviewFinancialsLoading}
            >
              Submit for Review
            </button>
          </div>
        );
      },
    });
  };

  const handleEmailSend = async () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-ui">
            <h3 className="text-center">
              Are you sure you want to send
              <br /> financials to all users receiving email at {currentLocation?.name}?
            </h3>
            <button className="button ReportControls-button mr-2" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button ReportControls-button-alert"
              onClick={() => {
                onClose();
                sendEmail();
              }}
              disabled={sendEmailPending}
            >
              Send Email
            </button>
          </div>
        );
      },
    });
  };

  const handleAIReport = async (e: SyntheticEvent<HTMLButtonElement>) => {
    setAiReport(!aiReport);
    if (!aiReport && reportQueue) {
      setReportQueue(false);
    }
  };

  const handleAIEditor = async (e: SyntheticEvent<HTMLButtonElement>) => {
    setAiEditor(!aiEditor);
  };

  const handleReportQueue = async (e: SyntheticEvent<HTMLButtonElement>) => {
    setReportQueue(!reportQueue);
    if (!reportQueue && aiReport) {
      setAiReport(false);
    }
  };

  const getReportChildren = (reports: NestedHoneReportSection[], flatArray: Report[] = []): Report[] => {
    for (const report of reports) {
      const maskedTitle = Array(report.level).fill(' * ').join('') + report.title;
      const maskedPercentageTitle = Array(report.level).fill(' * ').join('') + '% OF ' + report.title;

      flatArray.push({
        ...report,
        ...report.data?.map(data => (showMoneyValues ? currencyFormatter2Decimals(data?.amount) : '')),
        title: maskedTitle,
      });

      if (report.title !== '' && showPercentageValues) {
        flatArray.push({
          ...report,
          ...report.data?.map(data => percentageFormatter(data?.perc || 0)),
          title: maskedPercentageTitle,
        });
      }

      if (report.children) {
        getReportChildren(report.children, flatArray);
      }
    }
    return flatArray;
  };

  useEffect(() => {
    if (isAggrSideBySide) {
      setEnableMultiLocation(true);
      return;
    }
    setEnableMultiLocation(false);
  }, [isAggrSideBySide]);

  // Handle fetching report data when parameters change
  useEffect(() => {
    // Skip if required data is not available
    if (!availableTemplates || !templateId) return;

    // Generate report payload based on current parameters
    const reportPayload = generateReportPayload({
      currentLocation,
      dateRange,
      comparisonPeriods,
      compareLocations,
      availableTemplates,
    });

    if (!reportPayload) return;

    // Check if we already have a matching payload to avoid duplicate fetches
    // Note: generateReportPayload calls setReportPayload, so we need to compare BEFORE that was called
    // We use the lastPayloadString state to track what we last fetched
    const currentPayloadString = JSON.stringify(reportPayload);

    // Define the fetch report function
    const fetchReport = async () => {
      try {
        const response = getGroupReport(reportPayload);

        // Show toast notifications if validating
        if (validateParsed) {
          await toast.promise(
            // @ts-ignore
            response,
            {
              loading: 'Validating report, will take a few seconds...',
              success: 'Report successfully validated',
              error: 'Error validating report',
            },
            {
              style: { maxWidth: 'max(50vw, 350px)' },
            }
          );
        }
      } catch (error) {
        // Log the error but don't throw, to prevent the React component from crashing
        toast.error('Error fetching report:');
        console.error('Error fetching report:', error);
      }
    };

    // Create a debounced version of fetchReport to avoid multiple rapid requests
    const debouncedFetch = debounce(fetchReport, 300);

    // Fetch the report immediately on mount or when payload changes
    debouncedFetch();
    setLastPayloadString(currentPayloadString);

    // Cleanup function to cancel debounced fetch and reset store state
    return () => {
      debouncedFetch.cancel();
      useReportsStore.setState({
        selectedReport: undefined,
      });
    };
  }, [
    compareLocations,
    dateRange,
    totalParsed,
    differenceParsed,
    reverseParsed,
    breakdownPeriodsParsed,
    comparisonType,
    comparisonPeriods,
    budget,
    validateParsed,
    enableMultiLocation,
    consolidated,
    availableTemplates,
    templateId,
    // currentLocation is an object that changes reference frequently, use currentLocationId instead
    currentLocationId,
    // getGroupReport is a stable function from Zustand store, should not be in deps
    locationId,
    reportType,
  ]);

  const years = useMemo(
    () =>
      allReports.map(report => ({
        period: new Date(report.endDate).getFullYear(),
        reportId: report.id,
      })),
    [allReports]
  );

  const handleReportChange = (newReportId: string) => {
    if (newReportId === 'custom_range') {
      setOpenDateRangeModal(true);
      return;
    }

    setStatus('loading');
    setDateRange(undefined);

    const yearPlotted = years.find(year => year.reportId === newReportId);
    yearPlotted?.period && setYearPlotted(yearPlotted?.period);

    const { dateRange, total, difference, ...queryParams } = qs.parse(location.search.replace('?', ''));
    const newQueries = { ...queryParams };

    if (templateId) {
      newQueries.templateId = templateId;
    }
    if (validateParsed) {
      delete newQueries.validate;
    }
    useReportsStore.setState({ selectedReport: undefined });
    useActiveReportStore.setState({ currentReport: undefined });
    navigateTo({
      pathname: `/app/location/${currentLocationId}/report/${reportType}/${newReportId}`,
      search: qs.stringify(newQueries),
    });
  };

  // Disable smoothing for Balance Sheet and Cash Flow
  useEffect(() => {
    if (selectedReport && selectedReport?.type === HoneReportTypes.IncomeStatement)
      useReportGraphStore.setState({ extraReportId: undefined });
    if (
      selectedReport &&
      (selectedReport.type === HoneReportTypes.BalanceSheet || selectedReport.type === HoneReportTypes.CashFlow)
    ) {
      useActiveReportStore.setState({ smoothingEnabled: false });
    }
  }, [selectedReport]);

  const hasCharts =
    !isEmpty(allCategories) && selectedReport && ['P&L Comparison', 'Income Statement'].includes(selectedReport?.type);

  const showCharts = chartCollapsed === 'false' && auditMode !== 'true' && hasCharts && reportType !== 'ap-aging';

  const summaryReport = useReportGraphStore(state => state.summaryReport);

  const graphWidth = useMemo(() => {
    if (isYtd) {
      return 1200;
    }

    return 60 + 175 * ((summaryReport?.reportLabels || []).length ?? 3);
  }, [isYtd, selectedReport?.id, summaryReport?.reportLabels]);

  const getSelectedReportId = useCallback(() => {
    if (reportId) return reportId.includes('?') ? reportId.split('?')[0] : reportId;
    if (dateRange) return 'custom_range';

    if (status === 'pending') return 'loading';

    const index = filteredReports.findIndex(report => report.id === selectedReport?.id);
    if (index > -1) {
      return filteredReports[index].id;
    }
    return filteredReports[0]?.id;
  }, [selectedReport]);

  if (reportsError) {
    return (
      <div className="report-view add-padding">
        <div className="report-view--header">
          <div />
          <div className="report-view--header__location"></div>
        </div>
        <div className="report-view-container h-100 center">{reportsError}</div>
      </div>
    );
  }

  /**
   * Compares two objects and returns their differences.
   *
   * @param obj1 - The first object to compare
   * @param obj2 - The second object to compare
   * @param onlyCommon - When true, only return differences for properties that exist in both objects
   * @returns An object containing the differences between obj1 and obj2
   */
  const getObjectDiff = (
    obj1: Record<string, any>,
    obj2: Record<string, any>,
    onlyCommon: boolean = true
  ): Record<string, any> => {
    const diff: Record<string, any> = {};

    // Handle edge cases
    if (!obj1 && !obj2) return diff;
    if (!obj1) return obj2;
    if (!obj2) return obj1;

    // Compare properties and build difference object
    Object.keys(obj1).forEach(key => {
      if (onlyCommon) {
        // Only include differences where the property exists in both objects
        if (obj1[key] !== undefined && obj2[key] !== undefined && obj1[key] !== obj2[key]) {
          diff[key] = obj2[key];
        }
      } else {
        // Include all differences
        if (obj1[key] !== obj2[key]) {
          diff[key] = obj2[key];
        }
      }
    });

    return diff;
  };

  // Using refs to track previous state values without causing re-renders
  const prevOpenDateRangeModalRef = useRef(openDateRangeModal);
  const prevReportStatusRef = useRef(reportStatus);
  const prevSelectedReportRef = useRef(selectedReport);

  useEffect(() => {
    const wasOpen = prevOpenDateRangeModalRef.current;
    const prevStatus = prevReportStatusRef.current;

    // Compare URL parameters to detect changes
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const newParams = Object.fromEntries(urlParams.entries());
    const diff: any = getObjectDiff(paramList, newParams);

    // Determine if we need to refresh the interface
    const mustRefresh = firstLoad || Object.keys(diff).length > 0;

    // Handle modal state changes
    if (wasOpen && !openDateRangeModal) {
      setLoading(mustRefresh);
    }

    // Handle report status changes
    if (prevStatus !== reportStatus) {
      setFirstLoad(false);
      setLoading(mustRefresh);

      if (reportStatus === 'idle') {
        setLoading(false);
        setParamList(newParams);
      }
    }

    // Update ref values for next comparison
    prevOpenDateRangeModalRef.current = openDateRangeModal;
    prevReportStatusRef.current = reportStatus;
    prevSelectedReportRef.current = selectedReport;

    if (firstLoad) setLoading(true);
    setRedraw(mustRefresh);
  }, [reportStatus, selectedReport, openDateRangeModal, currentReport, paramList, firstLoad]);

  // Memoized NewTable component to prevent unnecessary re-renders
  const MemoizedNewTable = useMemo(() => {
    return <NewTable report={currentReport} reportId={getSelectedReportId()} loading={false} redraw={redraw} />;
  }, [currentReport, getSelectedReportId, redraw]);

  // Memoized container for report visualization components
  const MemoizedDrawReportViewContainer = useMemo(() => {
    if (!selectedReport) {
      return <></>;
    }

    return <div className="report-view-container">{MemoizedNewTable}</div>;
  }, [
    showCharts,
    selectedReport,
    graphWidth,
    isYtd,
    filteredReports,
    handleReportChange,
    columnHeaderTitle,
    customHeaders,
    MemoizedNewTable,
  ]);

  const Icon: any = selectedReport && selectedReport.type in MENU_ICONS ? MENU_ICONS[selectedReport.type] : null;

  const toggleValidate = () => {
    setShowValidationPanel(false);
  };

  const getReviewFinancialsTooltip = () => {
    if (validationReportData && (validationReportData.error > 0 || validationReportData.fail > 0)) {
      return 'Ready to review - fix validation errors';
    }

    return disabledReviewFinancials ? 'Ready to review - validate to enable' : 'Review Financials';
  };

  const getSendEmailTooltip = () => {
    if (validationReportData && (validationReportData.error > 0 || validationReportData.fail > 0)) {
      return 'Send Report via Email - fix validation errors';
    }

    return disabledSendEmail ? 'Send Report via Email - validate to enable' : 'Send email';
  };

  const adminTools: ActionProps[] = [
    {
      handleClick: handleValidateReport,
      tooltip: 'Validate report',
      icon: <IconValidateNew />,
      placement: 'bottom',
    },

    {
      handleClick: handleEmailSend,
      disabled: disabledSendEmail || sendEmailPending,
      tooltip: getSendEmailTooltip(),
      icon: <IconEmailNew />,
      placement: 'bottom',
    },
    {
      handleClick: handleReviewFinancials,
      disabled: disabledReviewFinancials || reviewFinancialsLoading,
      tooltip: getReviewFinancialsTooltip(),
      icon: <IconAuditNew />,
      placement: 'bottom',
    },
  ];

  return (
    <>
      {loading ? (
        <div className="report-view--table-container">
          <Loading />
        </div>
      ) : (
        <div className="report-view">
          <KSHeader />
          <div className="report-view--header add-padding">
            <div className="report-view--header__item">
              <span className="header-icon header-icon-bottom ">{Icon && <Icon />}</span>
              {isPLReport && currentLocation && (
                <>
                  {availableTemplates && (
                    <ReportPLDates
                      currentLocationId={currentLocation.id}
                      templateId={templateId}
                      availableTemplates={availableTemplates}
                      timeframe={timeframe as ReportTimeframe}
                      weekStart={currentLocation.weekStart}
                      reportConfigurationData={(reportConfiguration as ReportConfiguration)?.data}
                    />
                  )}
                  <KSCompareLocationsModal
                    currentLocationUser={currentLocationUser!}
                    allLocations={userLocations as HoneLocationUser[]}
                    handleApplyChanges={() => {
                      setEnableMultiLocation(true);
                    }}
                  />
                </>
              )}
            </div>
            <div className="report-view--header__item">
              <div>
                {currentReport && (
                  <STBTools
                    adminMode={isBookkeeper}
                    status={
                      ((currentReport?.id && statusUpdates[currentReport.id]) ||
                        currentReport?.status) as HoneReportStatus
                    }
                    onRefresh={(e: SyntheticEvent<HTMLButtonElement, Event>) => handleRefreshReport(e)}
                    onAIReport={
                      enableAiReport ? (e: SyntheticEvent<HTMLButtonElement, Event>) => handleAIReport(e) : undefined
                    }
                    onAIEditor={
                      enableAiReport ? (e: SyntheticEvent<HTMLButtonElement, Event>) => handleAIEditor(e) : undefined
                    }
                    onReportQueue={(e: SyntheticEvent<HTMLButtonElement, Event>) => handleReportQueue(e)}
                    onDownload={() => {}}
                    adminTools={adminTools}
                    report={currentReport}
                  />
                )}
              </div>
            </div>
          </div>
          {MemoizedDrawReportViewContainer}
          <SlidingPane
            onRequestClose={toggleValidate}
            title={`Validation results`}
            width={'75%'}
            isOpen={showValidationPane}
          >
            {validateReportError && <span>{validateReportError}</span>}
            {validationReportData && <ValidationReportResults reportData={validationReportData} />}
          </SlidingPane>
        </div>
      )}
    </>
  );
}

const PLReportViewWrapped = (props: Props) => (
  <LocalErrorBoundary>
    <PLReportView {...props} />
  </LocalErrorBoundary>
);

export default PLReportViewWrapped;
